import { useAuth } from "@mindlercare/auth-ui";
import {
  JWTClaimsRole,
  PermissionsEnum,
  SlotStatus,
} from "@mindlercare/schema";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQueryClient } = ReactQueryV5;
import { Select, Button, RadioGroup } from "@mindlercare/ui-web";
import { useMindlerApi } from "api/apiv2";
import { getMindlerApiV2Client } from "api/mindlerapi";
import { useAlertContext } from "components/booking/AlertContext";
import { useInvalidateSlots } from "components/booking/hooks/useInvalidateSlots";
import CONSTANTS from "core/constants";
import { logger } from "core/logger";
import type { Slot } from "core/types";
import { useCurrentUser } from "hooks/userHooks";
import { useEffect, useState } from "react";
import { TFunction, useTranslation } from "react-i18next";
import { styled } from "styled-components";

const dropdownOptions = (
  t: TFunction,
  slotStatuses: Record<string, string>,
) => {
  return Object.values(slotStatuses).map((reason, index) => ({
    label:
      reason === CONSTANTS.UPDATABLE_BOOKING_STATUS.NO_SHOW
        ? t("NO_SHOW_PATIENT")
        : t(reason),
    value: index,
    type: reason,
  }));
};

export const SlotStatusSection = ({
  slot,
  feeStatuses,
  getSlotStatuses,
  setSlotState,
}: {
  slot: Slot;
  feeStatuses: Slot["status"][];
  getSlotStatuses: (
    roles: JWTClaimsRole[],
    permissions: PermissionsEnum[],
  ) => Record<string, string>;
  setSlotState: (
    slotStatus: number,
    status: string,
    feeWaived: boolean,
  ) => void;
}) => {
  const { t } = useTranslation();
  const { showAlert } = useAlertContext();
  const invalidateSlots = useInvalidateSlots();
  const queryClient = useQueryClient();
  const api = useMindlerApi();
  const { roles } = useAuth();
  const { user } = useCurrentUser();
  const dropDownOptions = dropdownOptions(
    t,
    getSlotStatuses(
      (roles ?? []).map((role) => role.name),
      user?.permissions ?? [],
    ),
  );

  const [selectedSlotStatus, setSelectedSlotStatus] = useState(
    dropDownOptions.find((x) => x.type === slot.status) || dropDownOptions[0],
  );
  const [feeWaived, setFeeWaived] = useState(Boolean(slot.feeWaived));
  const shouldShowFeeButtons = feeStatuses.includes(
    selectedSlotStatus.type as Slot["status"],
  );

  useEffect(() => {
    if (!shouldShowFeeButtons) {
      setFeeWaived(false);
    }
  }, [shouldShowFeeButtons]);

  const handleSave = async () => {
    try {
      const updatedSlot =
        await getMindlerApiV2Client().slot.updateStatus.mutate({
          slotId: slot.slotId,
          feeWaived: Boolean(feeWaived),
          status: selectedSlotStatus.type as SlotStatus,
        });

      if (!updatedSlot) {
        logger.warn("Failed to update slot status, got back", updatedSlot);
        return;
      }

      // status should never be null, this is an error in generated DB schema
      if (updatedSlot.status === null || updatedSlot.feeWaived === null) {
        throw new Error(
          "Encountered null status after updating slot status, this should never happen.",
        );
      }

      setSlotState(
        updatedSlot.slotId,
        updatedSlot.status,
        Boolean(updatedSlot.feeWaived),
      );

      queryClient.setQueriesData(
        api.slots.getSlotWithPatientInfoBySlotId.queryFilter({
          slotId: slot.slotId,
        }),
        (old) =>
          old
            ? {
                ...old,
                status: updatedSlot.status,
                feeWaived: updatedSlot.feeWaived,
              }
            : old,
      );

      await invalidateSlots();
      showAlert(t("MEETING_STATUS_UPDATED"), "checkmark");
    } catch (error) {
      showAlert(t("MEETING_STATUS_SAVE_FAILED"), "warning");
      console.error(t("MEETING_STATUS_SAVE_FAILED"), error);
    }
  };

  const isDisabled =
    slot.status === selectedSlotStatus.type &&
    Boolean(slot.feeWaived) === Boolean(feeWaived);

  return (
    <Wrapper>
      <Select value={selectedSlotStatus.value?.toString()}>
        <MeetingStatusTrigger>
          {t(selectedSlotStatus.label)}
        </MeetingStatusTrigger>
        <Select.Options>
          {dropDownOptions.map((option) => (
            <Select.Option
              key={option.value}
              onClick={() => setSelectedSlotStatus(option)}
              value={option.value?.toString()}
            >
              {option.label}
            </Select.Option>
          ))}
        </Select.Options>
      </Select>

      {shouldShowFeeButtons && (
        <RadioButtonWrapper>
          <RadioGroup
            defaultValue={slot.feeWaived ? CONSTANTS.NO_FEE : CONSTANTS.FEE}
            onChange={(value: string) =>
              setFeeWaived(value === CONSTANTS.NO_FEE)
            }
          >
            <RadioGroup.RadioButton name={CONSTANTS.NO_FEE}>
              <span>{t(CONSTANTS.NO_FEE)}</span>
            </RadioGroup.RadioButton>
            <RadioGroup.RadioButton name={CONSTANTS.FEE}>
              <span>{t(CONSTANTS.FEE)}</span>
            </RadioGroup.RadioButton>
          </RadioGroup>
        </RadioButtonWrapper>
      )}

      <Button variant="primary" disabled={isDisabled} onClick={handleSave}>
        {t("Save")}
      </Button>
    </Wrapper>
  );
};

const MeetingStatusTrigger = styled(Select.Trigger)`
  min-width: 216px;
`;

const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 24px 40px;
`;

const RadioButtonWrapper = styled.div`
  margin: 25px 18px 0;
`;
